/**
 * Report Builder Page
 * Build, run, save, and export ad-hoc reports across data sources
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Play,
  Download,
  Save,
  Trash2,
  Loader2,
  FileText,
  Database,
  Filter,
  BookOpen,
} from 'lucide-react';

// --- Types ---

interface SavedReport {
  id: string;
  name: string;
  dataSource: string;
  filters: Record<string, unknown>;
  createdAt: string;
}

// --- Constants ---

const DATA_SOURCES = [
  'invoices',
  'accounts',
  'contacts',
  'deals',
  'tickets',
  'expenses',
  'activities',
  'payments',
  'contracts',
  'subscriptions',
];

// --- Component ---

export function ReportBuilderPage() {
  const queryClient = useQueryClient();

  // Report config state
  const [dataSource, setDataSource] = useState('invoices');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountIdFilter, setAccountIdFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // Results state
  const [results, setResults] = useState<Record<string, unknown>[] | null>(null);
  const [columns, setColumns] = useState<string[]>([]);

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reportName, setReportName] = useState('');

  // Fetch saved reports
  const { data: savedData } = useQuery({
    queryKey: ['savedReports'],
    queryFn: () => api.getSavedReports(),
  });
  const savedReports = (savedData?.data || []) as SavedReport[];

  // Execute report mutation
  const executeMutation = useMutation({
    mutationFn: (payload: { dataSource: string; filters: Record<string, unknown> }) =>
      api.executeReport(payload),
    onSuccess: (response) => {
      const rows = (response?.data || []) as Record<string, unknown>[];
      setResults(rows);
      if (rows.length > 0) {
        setColumns(Object.keys(rows[0]));
      } else {
        setColumns([]);
      }
    },
  });

  // Save report mutation
  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.saveReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedReports'] });
      setSaveDialogOpen(false);
      setReportName('');
    },
  });

  // Delete report mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedReports'] });
    },
  });

  function buildFilters(): Record<string, unknown> {
    const filters: Record<string, unknown> = {};
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    if (statusFilter) filters.status = statusFilter;
    if (accountIdFilter) filters.accountId = accountIdFilter;
    if (searchFilter) filters.search = searchFilter;
    return filters;
  }

  function handleRunReport() {
    executeMutation.mutate({ dataSource, filters: buildFilters() });
  }

  function handleSave() {
    saveMutation.mutate({
      name: reportName,
      dataSource,
      filters: buildFilters(),
    });
  }

  function handleLoadReport(report: SavedReport) {
    setDataSource(report.dataSource);
    setDateFrom((report.filters.dateFrom as string) || '');
    setDateTo((report.filters.dateTo as string) || '');
    setStatusFilter((report.filters.status as string) || '');
    setAccountIdFilter((report.filters.accountId as string) || '');
    setSearchFilter((report.filters.search as string) || '');
    // Auto-run the loaded report
    executeMutation.mutate({ dataSource: report.dataSource, filters: report.filters });
  }

  function handleExportCSV() {
    if (!results || results.length === 0) return;

    const headers = columns.join(',');
    const rows = results.map((row) =>
      columns
        .map((col) => {
          const val = row[col];
          const str = val === null || val === undefined ? '' : String(val);
          // Escape quotes and wrap in quotes if contains comma or quote
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(',')
    );
    const csv = [headers, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${dataSource}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Report Builder</h1>
        <p className="text-muted-foreground">
          Build custom reports from any data source
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 shrink-0 space-y-6">
          {/* Data Source */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Database className="h-4 w-4" />
                Data Source
              </div>
              <Select value={dataSource} onValueChange={setDataSource}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_SOURCES.map((ds) => (
                    <SelectItem key={ds} value={ds} className="capitalize">
                      {ds.charAt(0).toUpperCase() + ds.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Filter className="h-4 w-4" />
                Filters
              </div>
              <div>
                <Label className="text-xs">Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Input
                  placeholder="e.g., active"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Account ID</Label>
                <Input
                  placeholder="UUID"
                  value={accountIdFilter}
                  onChange={(e) => setAccountIdFilter(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <Input
                  placeholder="Keyword..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleRunReport}
                disabled={executeMutation.isPending}
              >
                {executeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Run Report
              </Button>
            </CardContent>
          </Card>

          {/* Saved Reports */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <BookOpen className="h-4 w-4" />
                  Saved Reports
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSaveDialogOpen(true)}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </div>
              {savedReports.length === 0 ? (
                <p className="text-xs text-muted-foreground">No saved reports</p>
              ) : (
                <div className="space-y-1">
                  {savedReports.map((report) => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between group"
                    >
                      <button
                        className="text-sm text-left hover:text-primary truncate flex-1"
                        onClick={() => handleLoadReport(report)}
                      >
                        {report.name}
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100"
                        onClick={() => {
                          if (confirm('Delete this saved report?'))
                            deleteMutation.mutate(report.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Results Area */}
        <div className="flex-1 min-w-0">
          {results === null ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-20">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Configure your data source and filters, then click &quot;Run Report&quot;
                </p>
              </CardContent>
            </Card>
          ) : results.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-20">
                <Database className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No results found. Try adjusting your filters.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-4 border-b">
                  <p className="text-sm text-muted-foreground">
                    {results.length} row{results.length !== 1 ? 's' : ''} returned
                  </p>
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
                <div className="overflow-auto max-h-[600px]">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                      <tr className="border-b">
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="text-left p-3 text-xs font-medium text-muted-foreground whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className="border-b hover:bg-muted/30"
                        >
                          {columns.map((col) => (
                            <td
                              key={col}
                              className="p-3 text-sm whitespace-nowrap max-w-[200px] truncate"
                            >
                              {row[col] === null || row[col] === undefined
                                ? '-'
                                : String(row[col])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Save Report Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Report</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Report Name</Label>
            <Input
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="e.g., Monthly Invoices Summary"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!reportName || saveMutation.isPending}
            >
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
